import { FloatingLabel, Form, InputGroup } from "react-bootstrap";
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { PolicyHomeDto } from "../../../model/PolicyHomeDto";
import { RootState } from "../../../../shared/store";
import { CustomerPolicyDetailLabels as cstPolicyUnderwritingLabel } from '../../../../shared/util/label';
import { Checkbox, Column, Row, Select } from "../../../../components";
import DatePicker from "react-datepicker";
import { CommonUtility } from "../../../../Services/CommonUtility";
import { useDispatch } from "react-redux";
import { updateSelectedPolicyDetails, updateLocationDetails, updateSelectedLOBDetails } from "../../../customer-slice/Customer-slice";
import { uuid } from "../../../../shared/util/uuid";
import { CustomerPolicyDetails as cstPolicyDetails } from '../../../../shared/util/constant'
import { CUSTOMER_POLICY_LOBCodeId } from '../../../../shared/util/constant'
import PolicyDetailService from "../CustomerEditPolicyDetailsService";
import "./CustomerPolicyUnderwriting.scss";

export interface ICustomerPolicyUnderwriting { 
    tabIndex?: number;
    validationErrors?: any;
}

const CustomerPolicyUnderwriting: React.FunctionComponent<ICustomerPolicyUnderwriting> = (props) => {
    let tbIndex = props.tabIndex;
    const { validationErrors } = props;
    const dispatch = useDispatch();
    const [formValidation, setFormValidation] = useState<any>({});
    const validationService = new PolicyDetailService();
    const policyId = useSelector((state: RootState) => state.customerSlice.policyId);
    const polEventId = useSelector((state: RootState) => state.customerSlice.polEventId);

    // Sync validation errors from props to local formValidation state
    useEffect(() => {
        if (validationErrors) {
            setFormValidation(validationErrors);
        }
    }, [validationErrors]);
    const policyDetailKey = CommonUtility.getPolicyDetailKey(policyId, polEventId);
    const customerpolicyDetail = useSelector((state: RootState) => state?.customerSlice);
    const policyDetails = customerpolicyDetail.selectedPolicyDetails?.[policyDetailKey];
    // Get selected location from Redux
    const selectedLocationDetails = useSelector((state: RootState) => state.customerSlice.selectedLocationDetails);
    // Get selected LOB to determine if Rating Information should be shown
    const selectedLOBDetails = useSelector((state: RootState) => state.customerSlice.selectedLOBDetails);
    const isHomeownersLOB = selectedLOBDetails?.lobCodeId === CUSTOMER_POLICY_LOBCodeId.HOMEOWNERS;
    // Get all the lookup values
    const occupancyTypeList = useSelector((state: RootState) => state.LookupState.occupancyTypeList);
    const residenceTypeList = useSelector((state: RootState) => state.LookupState.residenceTypeList);
    const constructionTypeList = useSelector((state: RootState) => state.LookupState.constructionTypeList);
    const roofMaterialTypeList = useSelector((state: RootState) => state.LookupState.roofMaterialTypeList);
    const sprinklerTypeList = useSelector((state: RootState) => state.LookupState.sprinklerTypeList);
    const dwellingConditionTypeList = useSelector((state: RootState) => state.LookupState.dwellingConditionTypeList);
    const stormShutterTypeList = useSelector((state: RootState) => state.LookupState.stormShutterTypeList);
    const heatTypeList = useSelector((state: RootState) => state.LookupState.heatTypeList);
    const dwellingLocTypeList = useSelector((state: RootState) => state.LookupState.dwellingLocTypeList);
    const secondaryHeatTypeList = useSelector((state: RootState) => state.LookupState.secondaryHeatTypeList);
    const foundationTypeList = useSelector((state: RootState) => state.LookupState.foundationTypeList);
    const protectionDeviceTypeList = useSelector((state: RootState) => state.LookupState.protectionDeviceTypeList);

    const occupancyTypeOptions = [...occupancyTypeList];
    const residenceTypeOptions = [...residenceTypeList]
    const constructionTypeOptions = [...constructionTypeList];
    const roofMaterialTypeOptions = [...roofMaterialTypeList];
    const sprinklerTypeOptions = [...sprinklerTypeList];
    const dwellingConditionTypeOptions = [...dwellingConditionTypeList];
    const stormShutterTypeOptions = [...stormShutterTypeList];
    const heatTypeOptions = [...heatTypeList];
    const dwellingLocTypeOptions = [...dwellingLocTypeList];
    const secondaryHeatTypeOptions = [...secondaryHeatTypeList];
    const foundationTypeOptions = [...foundationTypeList];
    const protectionDeviceTypeOptions = [...(protectionDeviceTypeList || [])];
    const renovationOptions = [
        { value: 'P', label: 'Part' },
        { value: 'F', label: 'Full' },
    ];

    const emptyHome: PolicyHomeDto = {
        id: uuid(),
        marketValue: null,
        yearBuilt: null,
        occupancyTypeId: null,
        residenceTypeId: null,
        totalSqFt: null,
        purchasePrice: null,
        purchaseDate: null,
        constructionTypeId: null,
        roofMaterialTypeId: null,
        sprinklerTypeId: null,
        dwellingConditionTypeId: null,
        dateInspected: null,
        protectionClass: "",
        stormShutterTypeId: null,
        heatTypeId: null,
        numberOfRooms: null,
        dwellingLocTypeId: null,
        secondaryHeatTypeId: null,
        numberOfStories: "",
        yearOccupied: "",
        isSwimPool: false,
        foundationTypeId: null,
        effDate: new Date()
    };

    const numberFields = [
        'marketValue', 'totalSqFt', 'numberOfRooms', 'purchasePrice',
        'groundFloorSqFt', 'numberOfApartments', 'numberOfFamilies',
        'numberOfHouseResidents', 'weeksRented', 'fireDistrictCode',
        'fireStationDistance', 'hydrantDistance', 'numberOfUnitsFireDivision',
        'numberOfFireDivision', 'ecPremGrp', 'fireECRate',
    ];

    if (!selectedLocationDetails) {
        return <div className="no-location-message">{cstPolicyDetails.NO_LOCATION_SELECTED}</div>;
    }

    // Check if location is new and unsaved (has temporary UUID, not saved to DB yet)
    if (selectedLocationDetails.isNewUnsaved === true) {
        return <div className="no-location-message">{cstPolicyDetails.LOCATION_NOT_SAVED}</div>;
    }

    // Get first policy home from location/default empty home if not found
    const policyHome: PolicyHomeDto = selectedLocationDetails.policyHomes && selectedLocationDetails.policyHomes.length > 0
    ? selectedLocationDetails.policyHomes[0]
    : emptyHome;


const updateLocation = (updatedHome: PolicyHomeDto) => {
    if (!selectedLocationDetails) return;

    // If no homes exist, add the new home; else, update the first home
    let updatedPolicyHomes;
    if (selectedLocationDetails.policyHomes && selectedLocationDetails.policyHomes.length > 0) {
        updatedPolicyHomes = [updatedHome, ...selectedLocationDetails.policyHomes.slice(1)];
    } else {
        updatedPolicyHomes = [updatedHome];
    }
    // 1. Update selectedLocationDetails
    const updatedLocation = { ...selectedLocationDetails, policyHomes: updatedPolicyHomes };
    dispatch(updateLocationDetails(updatedLocation));

    // 2. Update selectedLOBDetails (update the location in the LOB)
    const selectedLOB = customerpolicyDetail.selectedLOBDetails;
    if (selectedLOB && selectedLOB.policyLocations) {
        const updatedLocations = selectedLOB.policyLocations.map(loc =>
            loc.id === updatedLocation.id ? updatedLocation : loc
        );
        const updatedLOB = { ...selectedLOB, policyLocations: updatedLocations };
        dispatch(updateSelectedLOBDetails(updatedLOB));

        // 3. Update selectedPolicyDetails (update the LOB in policyLineOfBusinesses)
        if (policyDetails && policyDetails.policyLineOfBusinesses) {
            const updatedLOBs = policyDetails.policyLineOfBusinesses.map(lob =>
                lob.id === updatedLOB.id ? updatedLOB : lob
            );
            const updatedPolicyDetails = { ...policyDetails, policyLineOfBusinesses: updatedLOBs };
            dispatch(updateSelectedPolicyDetails({
                policyId,
                polEventId,
                policyDetails: updatedPolicyDetails
            }));
        }
    }
};

    const onFieldChange = (field: keyof PolicyHomeDto, value: any) => {
        if (!policyHome) return;
        let newValue = value;
        if (numberFields.includes(field as string)) {
            newValue = value === "" ? null : value;
        }
        if (
            field === "purchaseDate" ||
            field === "dateInspected"
        ) {
            newValue = value ? CommonUtility.datetimeToDateOnly(value) : undefined;
        }

        const updatedHome = { ...policyHome, [field]: newValue };
        updateLocation(updatedHome);
    };

    const handleFieldChange = (field: keyof PolicyHomeDto, value?: string) => {
        // When a renovation update dropdown is cleared, null out both the update
        // field and its paired year field so we don't persist an orphan year.
        const isRenovationUpdate =
            field === 'wiringUpdate' || field === 'plumbingUpdate' ||
            field === 'heatingUpdate' || field === 'roofingUpdate';
        if (isRenovationUpdate && !CommonUtility.isRenovationSelected(value)) {
            const yearField = field.replace('Update', 'Year') as keyof PolicyHomeDto;
            updateLocation({ ...policyHome, [field]: null, [yearField]: null });
            return;
        }
        onFieldChange(field, value || "");
    };

    return (
        <div className="additional-interest-form" style={{ marginBottom: 12 }}>
            <h6 className="section-title">{cstPolicyUnderwritingLabel.UNDERWRITING}</h6>
            <Row>
                <Column lg={3} md={3}>
                    <FloatingLabel controlId="marketValue" className="mb-3" label={cstPolicyUnderwritingLabel.MARKET_VALUE}>
                        <Form.Control
                            tabIndex={tbIndex++}
                            type="text"
                            name="marketValue"
                            placeholder={cstPolicyUnderwritingLabel.MARKET_VALUE}
                            value={policyHome?.marketValue || ''}
                            onChange={e => onFieldChange('marketValue', e.currentTarget.value)}
                        />
                    </FloatingLabel>
                </Column>
                <Column lg={3} md={3}>
                    <FloatingLabel controlId="yearBuilt" className="mb-3" label={cstPolicyUnderwritingLabel.YEAR_BUILT}>
                        <Form.Control
                            type="text"
                            tabIndex={tbIndex++}
                            name="yearBuilt"
                            placeholder={cstPolicyUnderwritingLabel.YEAR_BUILT}
                            value={policyHome?.yearBuilt || ''}
                            onChange={e => onFieldChange('yearBuilt', e.currentTarget.value)}
                        />
                    </FloatingLabel>
                </Column>
                <Column lg={3} md={3}>
                    <Select
                        id="occupancyTypeId"
                        tabIndex={tbIndex++}
                        label={cstPolicyUnderwritingLabel.OCCUPANCY}
                        options={occupancyTypeOptions}
                        defaultSelected={occupancyTypeOptions.filter(opt => opt.value === policyHome?.occupancyTypeId)}
                        onChange={values => onFieldChange('occupancyTypeId', values[0])}
                        placeholder={cstPolicyUnderwritingLabel.OCCUPANCY}
                        showAsFloatingLabel={true}
                        className='mb-3'
                    />
                </Column>
                <Column lg={3} md={3}>
                    <Select
                        id="residenceTypeId"
                        label={cstPolicyUnderwritingLabel.RESIDENCETYPE_USAGE}
                        tabIndex={tbIndex++}
                        options={residenceTypeOptions}
                        defaultSelected={residenceTypeOptions.filter(opt => opt.value === policyHome?.residenceTypeId)}
                        onChange={values => onFieldChange('residenceTypeId', values[0])}
                        placeholder={cstPolicyUnderwritingLabel.RESIDENCETYPE_USAGE}
                        showAsFloatingLabel={true}
                        className='mb-3'
                    />
                </Column>
            </Row>
            <Row>
                <Column lg={3} md={3}>
                    <FloatingLabel controlId="totalSqFt" className="mb-3" label={cstPolicyUnderwritingLabel.TOTAL_SQ_FT}>
                        <Form.Control
                            type="text"
                            name="totalSqFt"
                            tabIndex={tbIndex++}
                            placeholder={cstPolicyUnderwritingLabel.TOTAL_SQ_FT}
                            value={policyHome?.totalSqFt || ''}
                            onChange={e => onFieldChange('totalSqFt', e.currentTarget.value)}
                        />
                    </FloatingLabel>
                </Column>
                <Column lg={3} md={3}>
                    <FloatingLabel controlId="purchasePrice" className="mb-3" label={cstPolicyUnderwritingLabel.PURCHASE_PRICE}>
                        <Form.Control
                            type="text"
                            tabIndex={tbIndex++}
                            name="purchasePrice"
                            placeholder={cstPolicyUnderwritingLabel.PURCHASE_PRICE}
                            value={policyHome?.purchasePrice || ''}
                            onChange={e => onFieldChange('purchasePrice', e.currentTarget.value)}
                        />
                    </FloatingLabel>
                </Column>
                <Column lg={3} md={3}>
                    <InputGroup className="mb-3">
                        <InputGroup.Text>
                            {cstPolicyUnderwritingLabel.PURCHASE_DATE}
                        </InputGroup.Text>
                        <DatePicker
                            dateFormatCalendar="LLLL yyyy"
                            tabIndex={tbIndex++}
                            dropdownMode="scroll"
                            selected={CommonUtility.datetimeToDateOnlyObject(policyHome?.purchaseDate)}
                            onChange={date => onFieldChange('purchaseDate', date)}
                            className="form-control"
                        />
                        <InputGroup.Text>
                            <i className="bi bi-calendar3"></i>
                        </InputGroup.Text>
                    </InputGroup>
                </Column>
                <Column lg={3} md={3}>
                    <Select
                        id="constructionTypeId"
                        label={cstPolicyUnderwritingLabel.CONSTRUCTION_TYPE}
                        options={constructionTypeOptions}
                        tabIndex={tbIndex++}
                        defaultSelected={constructionTypeOptions.filter(opt => opt.value === policyHome?.constructionTypeId)}
                        onChange={values => onFieldChange('constructionTypeId', values[0])}
                        placeholder={cstPolicyUnderwritingLabel.CONSTRUCTION_TYPE}
                        showAsFloatingLabel={true}
                        className='mb-3'
                    />
                </Column>
            </Row>
            <Row>
                <Column lg={3} md={3}>
                    <Select
                        id="roofMaterialTypeId"
                        label={cstPolicyUnderwritingLabel.ROOF_MATERIAL}
                        options={roofMaterialTypeOptions}
                        tabIndex={tbIndex++}
                        defaultSelected={roofMaterialTypeOptions.filter(opt => opt.value === policyHome?.roofMaterialTypeId)}
                        onChange={values => onFieldChange('roofMaterialTypeId', values[0])}
                        placeholder={cstPolicyUnderwritingLabel.ROOF_MATERIAL}
                        showAsFloatingLabel={true}
                        className='mb-3'
                    />
                </Column>
                <Column lg={3} md={3}>
                    <Select
                        id="foundationTypeId"
                        label={cstPolicyUnderwritingLabel.FOUNDATION}
                        tabIndex={tbIndex++}
                        options={foundationTypeOptions}
                        defaultSelected={foundationTypeOptions.filter(opt => opt.value === policyHome?.foundationTypeId)}
                        onChange={values => onFieldChange('foundationTypeId', values[0])}
                        placeholder={cstPolicyUnderwritingLabel.FOUNDATION}
                        showAsFloatingLabel={true}
                        className='mb-3'
                    />
                </Column>
                <Column lg={3} md={3}>
                    <Select
                        id="sprinklerTypeId"
                        label={cstPolicyUnderwritingLabel.SPRINKLER}
                        options={sprinklerTypeOptions}
                        tabIndex={tbIndex++}
                        defaultSelected={sprinklerTypeOptions.filter(opt => opt.value === policyHome?.sprinklerTypeId)}
                        onChange={values => onFieldChange('sprinklerTypeId', values[0])}
                        placeholder={cstPolicyUnderwritingLabel.SPRINKLER}
                        showAsFloatingLabel={true}
                        className='mb-3'
                    />
                </Column>
                <Column lg={3} md={3}>
                    <Select
                        id="dwellingConditionTypeId"
                        label={cstPolicyUnderwritingLabel.DWELLING_COVERAGE}
                        tabIndex={tbIndex++}
                        options={dwellingConditionTypeOptions}
                        defaultSelected={dwellingConditionTypeOptions.filter(opt => opt.value === policyHome?.dwellingConditionTypeId)}
                        onChange={values => onFieldChange('dwellingConditionTypeId', values[0])}
                        placeholder={cstPolicyUnderwritingLabel.DWELLING_COVERAGE}
                        showAsFloatingLabel={true}
                        className='mb-3'
                    />
                </Column>
            </Row>
            <Row>
                <Column lg={3} md={3}>
                    <InputGroup className="mb-3">
                        <InputGroup.Text>
                            {cstPolicyUnderwritingLabel.Date_Inspected}
                        </InputGroup.Text>
                        <DatePicker
                            dateFormatCalendar="LLLL yyyy"
                            tabIndex={tbIndex++}
                            dropdownMode="scroll"
                            selected={CommonUtility.datetimeToDateOnlyObject(policyHome?.dateInspected)}
                            onChange={date => onFieldChange('dateInspected', date)}
                            className="form-control"
                        />
                        <InputGroup.Text>
                            <i className="bi bi-calendar3"></i>
                        </InputGroup.Text>
                    </InputGroup>
                </Column>
                <Column lg={3} md={3}>
                    <FloatingLabel controlId="protectionClass" className="mb-3" label={cstPolicyUnderwritingLabel.PROTECTION_CLASS}>
                        <Form.Control
                            type="text"
                            name="protectionClass"
                            tabIndex={tbIndex++}
                            placeholder={cstPolicyUnderwritingLabel.PROTECTION_CLASS}
                            value={policyHome?.protectionClass || ''}
                            onChange={e => onFieldChange('protectionClass', e.currentTarget.value)}
                        />
                    </FloatingLabel>
                </Column>
                <Column lg={3} md={3}>
                    <Select
                        id="stormShutterTypeId"
                        label={cstPolicyUnderwritingLabel.Storm_Shutters}
                        tabIndex={tbIndex++}
                        options={stormShutterTypeOptions}
                        defaultSelected={stormShutterTypeOptions.filter(opt => opt.value === policyHome?.stormShutterTypeId)}
                        onChange={values => onFieldChange('stormShutterTypeId', values[0])}
                        placeholder={cstPolicyUnderwritingLabel.Storm_Shutters}
                        showAsFloatingLabel={true}
                        className='mb-3'
                    />
                </Column>
                <Column lg={3} md={3}>
                    <Select
                        id="heatTypeId"
                        label={cstPolicyUnderwritingLabel.Heat_Type}
                        options={heatTypeOptions}
                        tabIndex={tbIndex++}
                        defaultSelected={heatTypeOptions.filter(opt => opt.value === policyHome?.heatTypeId)}
                        onChange={values => onFieldChange('heatTypeId', values[0])}
                        placeholder={cstPolicyUnderwritingLabel.Heat_Type}
                        showAsFloatingLabel={true}
                        className='mb-3'
                    />
                </Column>
            </Row>
            <Row>
                <Column lg={3} md={3}>
                    <FloatingLabel controlId="numberOfRooms" className="mb-3" label={cstPolicyUnderwritingLabel.NO_OF_ROOMS}>
                        <Form.Control
                            type="text"
                            name="numberOfRooms"
                            tabIndex={tbIndex++}
                            placeholder={cstPolicyUnderwritingLabel.NO_OF_ROOMS}
                            value={policyHome?.numberOfRooms || ''}
                            onChange={e => onFieldChange('numberOfRooms', e.currentTarget.value)}
                        />
                    </FloatingLabel>
                </Column>
                <Column lg={3} md={3}>
                    <Select
                        id="dwellingLocTypeId"
                        label={cstPolicyUnderwritingLabel.Dwelling_Loc}
                        tabIndex={tbIndex++}
                        options={dwellingLocTypeOptions}
                        defaultSelected={dwellingLocTypeOptions.filter(opt => opt.value === policyHome?.dwellingLocTypeId)}
                        onChange={values => onFieldChange('dwellingLocTypeId', values[0])}
                        placeholder={cstPolicyUnderwritingLabel.Dwelling_Loc}
                        showAsFloatingLabel={true}
                        className='mb-3'
                    />
                </Column>
                <Column lg={3} md={3}>
                    <Select
                        id="secondaryHeatTypeId"
                        label={cstPolicyUnderwritingLabel.Secondary_Heat}
                        options={secondaryHeatTypeOptions}
                        tabIndex={tbIndex++}
                        defaultSelected={secondaryHeatTypeOptions.filter(opt => opt.value === policyHome?.secondaryHeatTypeId)}
                        onChange={values => onFieldChange('secondaryHeatTypeId', values[0])}
                        placeholder={cstPolicyUnderwritingLabel.Secondary_Heat}
                        showAsFloatingLabel={true}
                        className='mb-3'
                    />
                </Column>
                                <Column lg={3} md={3}>
                    <FloatingLabel controlId="numberOfStories" className="mb-3" label={cstPolicyUnderwritingLabel.No_of_Stories}>
                        <Form.Control
                            type="text"
                            tabIndex={tbIndex++}
                            name="numberOfStories"
                            placeholder={cstPolicyUnderwritingLabel.No_of_Stories}
                            value={policyHome?.numberOfStories || ''}
                            onChange={e => onFieldChange('numberOfStories', e.currentTarget.value)}
                        />
                    </FloatingLabel>
                </Column>
            </Row>
            <Row>
                <Column lg={3} md={3}>
                    <FloatingLabel controlId="yearOccupied" className="mb-3" label={cstPolicyUnderwritingLabel.Years_Occupied}>
                        <Form.Control
                            type="text"
                            tabIndex={tbIndex++}
                            name="yearOccupied"
                            placeholder={cstPolicyUnderwritingLabel.Years_Occupied}
                            value={policyHome?.yearOccupied || ''}
                            onChange={e => onFieldChange('yearOccupied', e.currentTarget.value)}
                        />
                    </FloatingLabel>
                </Column>
                <Column lg={3} md={3}>
                    <Checkbox
                        id='isSwimPool'
                        className="checkbox-swim-pool"
                        tabIndex={(tbIndex++).toString()}
                        label={cstPolicyUnderwritingLabel.Swimming_Pool}
                        checked={policyHome?.isSwimPool|| false}
                        onChange={(event) => {
                            if (!policyHome) return;
                            const newSwimPoolValue = !policyHome?.isSwimPool;
                            // Single update: combine isSwimPool and isDivingBoard reset into one
                            const updatedHome = { 
                                ...policyHome, 
                                isSwimPool: newSwimPoolValue,
                                // If unchecking Swimming Pool, also reset Diving Board to unchecked
                                isDivingBoard: newSwimPoolValue ? policyHome.isDivingBoard : false
                            };
                            updateLocation(updatedHome);
                        }}
                    />
                </Column>

                <Column lg={3} md={3}>
                    {policyHome?.isSwimPool && (
                        <Checkbox
                            id='isDivingBoard'
                            className="checkbox-diving-board"
                            tabIndex={(tbIndex++).toString()}
                            label={cstPolicyUnderwritingLabel.DIVING_BOARD}
                            checked={policyHome?.isDivingBoard || false}
                            onChange={() => onFieldChange('isDivingBoard', !policyHome?.isDivingBoard)}
                        />
                    )}
                </Column>
            </Row>

            {/* Rating Information Section - Only for Homeowners LOB */}
            {isHomeownersLOB && (
            <>
            <h6 className="section-title" style={{ marginTop: 16 }}>{cstPolicyUnderwritingLabel.RATING_INFORMATION}</h6>
            {/* Row 1: Ground Floor Area, # in Household, # Families, # of Apts */}
            <Row>
                <Column lg={3} md={3}>
                    <FloatingLabel controlId="groundFloorSqFt" className="mb-3" label={cstPolicyUnderwritingLabel.GROUND_FLOOR_AREA}>
                        <Form.Control
                            type="text"
                            name="groundFloorSqFt"
                            tabIndex={tbIndex++}
                            placeholder={cstPolicyUnderwritingLabel.GROUND_FLOOR_AREA}
                            value={policyHome?.groundFloorSqFt || ''}
                            onChange={e => onFieldChange('groundFloorSqFt', e.currentTarget.value)}
                        />
                    </FloatingLabel>
                </Column>
                <Column lg={3} md={3}>
                    <FloatingLabel controlId="numberOfHouseResidents" className="mb-3" label={cstPolicyUnderwritingLabel.NUMBER_IN_HOUSEHOLD}>
                        <Form.Control
                            type="text"
                            name="numberOfHouseResidents"
                            tabIndex={tbIndex++}
                            placeholder={cstPolicyUnderwritingLabel.NUMBER_IN_HOUSEHOLD}
                            value={policyHome?.numberOfHouseResidents || ''}
                            onChange={e => onFieldChange('numberOfHouseResidents', e.currentTarget.value)}
                        />
                    </FloatingLabel>
                </Column>
                <Column lg={3} md={3}>
                    <FloatingLabel controlId="numberOfFamilies" className="mb-3" label={cstPolicyUnderwritingLabel.NUMBER_OF_FAMILIES}>
                        <Form.Control
                            type="text"
                            name="numberOfFamilies"
                            tabIndex={tbIndex++}
                            placeholder={cstPolicyUnderwritingLabel.NUMBER_OF_FAMILIES}
                            value={policyHome?.numberOfFamilies || ''}
                            onChange={e => onFieldChange('numberOfFamilies', e.currentTarget.value)}
                        />
                    </FloatingLabel>
                </Column>
                <Column lg={3} md={3}>
                    <FloatingLabel controlId="numberOfApartments" className="mb-3" label={cstPolicyUnderwritingLabel.NUMBER_OF_APTS}>
                        <Form.Control
                            type="text"
                            name="numberOfApartments"
                            tabIndex={tbIndex++}
                            placeholder={cstPolicyUnderwritingLabel.NUMBER_OF_APTS}
                            value={policyHome?.numberOfApartments || ''}
                            onChange={e => onFieldChange('numberOfApartments', e.currentTarget.value)}
                        />
                    </FloatingLabel>
                </Column>
            </Row>
            {/* Row 2: # Weeks Rented, Territory Code, Tax Code, Fire District */}
            <Row>
                <Column lg={3} md={3}>
                    <FloatingLabel controlId="weeksRented" className="mb-3" label={cstPolicyUnderwritingLabel.WEEKS_RENTED}>
                        <Form.Control
                            type="text"
                            name="weeksRented"
                            tabIndex={tbIndex++}
                            placeholder={cstPolicyUnderwritingLabel.WEEKS_RENTED}
                            value={policyHome?.weeksRented || ''}
                            onChange={e => onFieldChange('weeksRented', e.currentTarget.value)}
                        />
                    </FloatingLabel>
                </Column>
                <Column lg={3} md={3}>
                    <FloatingLabel controlId="territoryCode" className="mb-3" label={cstPolicyUnderwritingLabel.TERRITORY_CODE}>
                        <Form.Control
                            type="text"
                            name="territoryCode"
                            tabIndex={tbIndex++}
                            placeholder={cstPolicyUnderwritingLabel.TERRITORY_CODE}
                            value={policyHome?.territoryCode || ''}
                            onChange={e => onFieldChange('territoryCode', e.currentTarget.value)}
                        />
                    </FloatingLabel>
                </Column>
                <Column lg={3} md={3}>
                    <FloatingLabel controlId="taxCode" className="mb-3" label={cstPolicyUnderwritingLabel.TAX_CODE}>
                        <Form.Control
                            type="text"
                            name="taxCode"
                            tabIndex={tbIndex++}
                            placeholder={cstPolicyUnderwritingLabel.TAX_CODE}
                            value={policyHome?.taxCode || ''}
                            onChange={e => onFieldChange('taxCode', e.currentTarget.value)}
                        />
                    </FloatingLabel>
                </Column>
                <Column lg={3} md={3}>
                    <FloatingLabel controlId="fireDistrictName" className="mb-3" label={cstPolicyUnderwritingLabel.FIRE_DISTRICT}>
                        <Form.Control
                            type="text"
                            name="fireDistrictName"
                            tabIndex={tbIndex++}
                            placeholder={cstPolicyUnderwritingLabel.FIRE_DISTRICT}
                            value={policyHome?.fireDistrictName || ''}
                            onChange={e => onFieldChange('fireDistrictName', e.currentTarget.value)}
                        />
                    </FloatingLabel>
                </Column>
            </Row>
            {/* Row 3: District Code, # Units Fire Div, Miles to Fire Station, Feet to Hydrant */}
            <Row>
                <Column lg={3} md={3}>
                    <FloatingLabel controlId="fireDistrictCode" className="mb-3" label={cstPolicyUnderwritingLabel.DISTRICT_CODE}>
                        <Form.Control
                            type="text"
                            name="fireDistrictCode"
                            tabIndex={tbIndex++}
                            placeholder={cstPolicyUnderwritingLabel.DISTRICT_CODE}
                            value={policyHome?.fireDistrictCode || ''}
                            onChange={e => onFieldChange('fireDistrictCode', e.currentTarget.value)}
                        />
                    </FloatingLabel>
                </Column>
                <Column lg={3} md={3}>
                    <FloatingLabel controlId="numberOfUnitsFireDivision" className="mb-3" label={cstPolicyUnderwritingLabel.UNITS_FIRE_DIV}>
                        <Form.Control
                            type="text"
                            name="numberOfUnitsFireDivision"
                            tabIndex={tbIndex++}
                            placeholder={cstPolicyUnderwritingLabel.UNITS_FIRE_DIV}
                            value={policyHome?.numberOfUnitsFireDivision || ''}
                            onChange={e => onFieldChange('numberOfUnitsFireDivision', e.currentTarget.value)}
                        />
                    </FloatingLabel>
                </Column>
                <Column lg={3} md={3}>
                    <FloatingLabel controlId="fireStationDistance" className="mb-3" label={cstPolicyUnderwritingLabel.MILES_TO_FIRE_STATION}>
                        <Form.Control
                            type="text"
                            name="fireStationDistance"
                            tabIndex={tbIndex++}
                            placeholder={cstPolicyUnderwritingLabel.MILES_TO_FIRE_STATION}
                            value={policyHome?.fireStationDistance || ''}
                            onChange={e => onFieldChange('fireStationDistance', e.currentTarget.value)}
                        />
                    </FloatingLabel>
                </Column>
                <Column lg={3} md={3}>
                    <FloatingLabel controlId="hydrantDistance" className="mb-3" label={cstPolicyUnderwritingLabel.FEET_TO_HYDRANT}>
                        <Form.Control
                            type="text"
                            name="hydrantDistance"
                            tabIndex={tbIndex++}
                            placeholder={cstPolicyUnderwritingLabel.FEET_TO_HYDRANT}
                            value={policyHome?.hydrantDistance || ''}
                            onChange={e => onFieldChange('hydrantDistance', e.currentTarget.value)}
                        />
                    </FloatingLabel>
                </Column>
            </Row>
            {/* Row 4: # Fire Divisions, EC Prem Group, Fire/EC Rate, Building Code */}
            <Row>
                <Column lg={3} md={3}>
                    <FloatingLabel controlId="numberOfFireDivision" className="mb-3" label={cstPolicyUnderwritingLabel.FIRE_DIVISIONS}>
                        <Form.Control
                            type="text"
                            name="numberOfFireDivision"
                            tabIndex={tbIndex++}
                            placeholder={cstPolicyUnderwritingLabel.FIRE_DIVISIONS}
                            value={policyHome?.numberOfFireDivision || ''}
                            onChange={e => onFieldChange('numberOfFireDivision', e.currentTarget.value)}
                        />
                    </FloatingLabel>
                </Column>
                <Column lg={3} md={3}>
                    <FloatingLabel controlId="ecPremGrp" className="mb-3" label={cstPolicyUnderwritingLabel.EC_PREM_GROUP}>
                        <Form.Control
                            type="text"
                            name="ecPremGrp"
                            tabIndex={tbIndex++}
                            placeholder={cstPolicyUnderwritingLabel.EC_PREM_GROUP}
                            value={policyHome?.ecPremGrp || ''}
                            onChange={e => onFieldChange('ecPremGrp', e.currentTarget.value)}
                        />
                    </FloatingLabel>
                </Column>
                <Column lg={3} md={3}>
                    <FloatingLabel controlId="fireECRate" className="mb-3" label={cstPolicyUnderwritingLabel.FIRE_EC_RATE}>
                        <Form.Control
                            type="text"
                            name="fireECRate"
                            tabIndex={tbIndex++}
                            placeholder={cstPolicyUnderwritingLabel.FIRE_EC_RATE}
                            value={policyHome?.fireECRate || ''}
                            onChange={e => onFieldChange('fireECRate', e.currentTarget.value)}
                        />
                    </FloatingLabel>
                </Column>
                <Column lg={3} md={3}>
                    <FloatingLabel controlId="bldgCodeGrade" className="mb-3" label={cstPolicyUnderwritingLabel.BUILDING_CODE}>
                        <Form.Control
                            type="text"
                            name="bldgCodeGrade"
                            tabIndex={tbIndex++}
                            placeholder={cstPolicyUnderwritingLabel.BUILDING_CODE}
                            value={policyHome?.bldgCodeGrade || ''}
                            onChange={e => onFieldChange('bldgCodeGrade', e.currentTarget.value)}
                        />
                    </FloatingLabel>
                </Column>
            </Row>
            {/* Row 5: Place Code, State Requirements */}
            <Row>
                <Column lg={3} md={3}>
                    <FloatingLabel controlId="placeCode" className="mb-3" label={cstPolicyUnderwritingLabel.PLACE_CODE}>
                        <Form.Control
                            type="text"
                            name="placeCode"
                            tabIndex={tbIndex++}
                            placeholder={cstPolicyUnderwritingLabel.PLACE_CODE}
                            value={policyHome?.placeCode || ''}
                            onChange={e => onFieldChange('placeCode', e.currentTarget.value)}
                        />
                    </FloatingLabel>
                </Column>
                <Column lg={3} md={3}>
                    <FloatingLabel controlId="stateRequirements" className="mb-3" label={cstPolicyUnderwritingLabel.STATE_REQUIREMENTS}>
                        <Form.Control
                            type="text"
                            name="stateRequirements"
                            tabIndex={tbIndex++}
                            placeholder={cstPolicyUnderwritingLabel.STATE_REQUIREMENTS}
                            value={policyHome?.stateRequirements || ''}
                            onChange={e => onFieldChange('stateRequirements', e.currentTarget.value)}
                        />
                    </FloatingLabel>
                </Column>
            </Row>
            {/* Protection Device Type Section */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16 }}>
                <h6 className="section-title" style={{ margin: 0 }}>{cstPolicyUnderwritingLabel.PROTECTION_DEVICE_TYPE}</h6>
                <Checkbox
                    id='isProtClassImp'
                    className="checkbox-prot-class"
                    tabIndex={(tbIndex++).toString()}
                    label={cstPolicyUnderwritingLabel.IMPROVED_PROT_CLASS}
                    checked={policyHome?.isProtClassImp || false}
                    onChange={() => onFieldChange('isProtClassImp', !policyHome?.isProtClassImp)}
                />
            </div>
            <Row style={{ marginTop: 12 }}>
                <Column lg={3} md={3}>
                    <Select
                        id="burglarProtectionTypeId"
                        label={cstPolicyUnderwritingLabel.BURGLAR}
                        tabIndex={tbIndex++}
                        options={protectionDeviceTypeOptions}
                        defaultSelected={protectionDeviceTypeOptions.filter(opt => opt.value === policyHome?.burglarProtectionTypeId)}
                        onChange={values => onFieldChange('burglarProtectionTypeId', values[0])}
                        placeholder={cstPolicyUnderwritingLabel.BURGLAR}
                        showAsFloatingLabel={true}
                        className='mb-3'
                    />
                </Column>
                <Column lg={3} md={3}>
                    <Select
                        id="fireProtectionTypeId"
                        label={cstPolicyUnderwritingLabel.FIRE_PROTECTION}
                        tabIndex={tbIndex++}
                        options={protectionDeviceTypeOptions}
                        defaultSelected={protectionDeviceTypeOptions.filter(opt => opt.value === policyHome?.fireProtectionTypeId)}
                        onChange={values => onFieldChange('fireProtectionTypeId', values[0])}
                        placeholder={cstPolicyUnderwritingLabel.FIRE_PROTECTION}
                        showAsFloatingLabel={true}
                        className='mb-3'
                    />
                </Column>
                <Column lg={3} md={3}>
                    <Select
                        id="smokeProtectionTypeId"
                        label={cstPolicyUnderwritingLabel.SMOKE}
                        tabIndex={tbIndex++}
                        options={protectionDeviceTypeOptions}
                        defaultSelected={protectionDeviceTypeOptions.filter(opt => opt.value === policyHome?.smokeProtectionTypeId)}
                        onChange={values => onFieldChange('smokeProtectionTypeId', values[0])}
                        placeholder={cstPolicyUnderwritingLabel.SMOKE}
                        showAsFloatingLabel={true}
                        className='mb-3'
                    />
                </Column>
                <Column lg={3} md={3}>
                    <Select
                        id="temperatureProtectionTypeId"
                        label={cstPolicyUnderwritingLabel.TEMP}
                        tabIndex={tbIndex++}
                        options={protectionDeviceTypeOptions}
                        defaultSelected={protectionDeviceTypeOptions.filter(opt => opt.value === policyHome?.temperatureProtectionTypeId)}
                        onChange={values => onFieldChange('temperatureProtectionTypeId', values[0])}
                        placeholder={cstPolicyUnderwritingLabel.TEMP}
                        showAsFloatingLabel={true}
                        className='mb-3'
                    />
                </Column>
            </Row>
            {/* Renovation Update Section */}
            <h6 className="section-title" style={{ marginTop: 16 }}>{cstPolicyUnderwritingLabel.RENOVATION_UPDATE}</h6>
            <Row>
                <Column lg={3} md={3}>
                    <Select
                        id="wiringUpdate"
                        label={cstPolicyUnderwritingLabel.WIRING_UPDATE}
                        tabIndex={tbIndex++}
                        options={renovationOptions}
                        defaultSelected={renovationOptions.filter(opt => opt.value === policyHome?.wiringUpdate)}
                        onChange={values => handleFieldChange('wiringUpdate', values[0])}
                        placeholder={cstPolicyUnderwritingLabel.WIRING_UPDATE}
                        showAsFloatingLabel={true}
                        className='mb-3'
                    />
                </Column>
                <Column lg={3} md={3}>
                    <FloatingLabel controlId="wiringYear" className="mb-3" label={cstPolicyUnderwritingLabel.WIRING_YEAR}>
                        <Form.Control
                            type="text"
                            name="wiringYear"
                            tabIndex={tbIndex++}
                            placeholder={cstPolicyUnderwritingLabel.WIRING_YEAR}
                            value={policyHome?.wiringYear || ''}
                            disabled={!CommonUtility.isRenovationSelected(policyHome?.wiringUpdate)}
                            maxLength={4}
                            pattern="[0-9]*"
                            onChange={e => handleFieldChange('wiringYear', e.currentTarget.value.replace(/[^0-9]/g, ''))}
                            isInvalid={formValidation['wiringYear']?.isValid == false}
                        />
                        <Form.Control.Feedback type="invalid">
                            {formValidation['wiringYear']?.warningText}
                        </Form.Control.Feedback>
                    </FloatingLabel>
                </Column>
                <Column lg={3} md={3}>
                    <Select
                        id="plumbingUpdate"
                        label={cstPolicyUnderwritingLabel.PLUMBING_UPDATE}
                        tabIndex={tbIndex++}
                        options={renovationOptions}
                        defaultSelected={renovationOptions.filter(opt => opt.value === policyHome?.plumbingUpdate)}
                        onChange={values => handleFieldChange('plumbingUpdate', values[0])}
                        placeholder={cstPolicyUnderwritingLabel.PLUMBING_UPDATE}
                        showAsFloatingLabel={true}
                        className='mb-3'
                    />
                </Column>
                <Column lg={3} md={3}>
                    <FloatingLabel controlId="plumbingYear" className="mb-3" label={cstPolicyUnderwritingLabel.PLUMBING_YEAR}>
                        <Form.Control
                            type="text"
                            name="plumbingYear"
                            tabIndex={tbIndex++}
                            placeholder={cstPolicyUnderwritingLabel.PLUMBING_YEAR}
                            value={policyHome?.plumbingYear || ''}
                            disabled={!CommonUtility.isRenovationSelected(policyHome?.plumbingUpdate)}
                            maxLength={4}
                            pattern="[0-9]*"
                            onChange={e => handleFieldChange('plumbingYear', e.currentTarget.value.replace(/[^0-9]/g, ''))}
                            isInvalid={formValidation['plumbingYear']?.isValid == false}
                        />
                        <Form.Control.Feedback type="invalid">
                            {formValidation['plumbingYear']?.warningText}
                        </Form.Control.Feedback>
                    </FloatingLabel>
                </Column>
            </Row>
            <Row>
                <Column lg={3} md={3}>
                    <Select
                        id="heatingUpdate"
                        label={cstPolicyUnderwritingLabel.HEATING_UPDATE}
                        tabIndex={tbIndex++}
                        options={renovationOptions}
                        defaultSelected={renovationOptions.filter(opt => opt.value === policyHome?.heatingUpdate)}
                        onChange={values => handleFieldChange('heatingUpdate', values[0])}
                        placeholder={cstPolicyUnderwritingLabel.HEATING_UPDATE}
                        showAsFloatingLabel={true}
                        className='mb-3'
                    />
                </Column>
                <Column lg={3} md={3}>
                    <FloatingLabel controlId="heatingYear" className="mb-3" label={cstPolicyUnderwritingLabel.HEATING_YEAR}>
                        <Form.Control
                            type="text"
                            name="heatingYear"
                            tabIndex={tbIndex++}
                            placeholder={cstPolicyUnderwritingLabel.HEATING_YEAR}
                            value={policyHome?.heatingYear || ''}
                            disabled={!CommonUtility.isRenovationSelected(policyHome?.heatingUpdate)}
                            maxLength={4}
                            pattern="[0-9]*"
                            onChange={e => handleFieldChange('heatingYear', e.currentTarget.value.replace(/[^0-9]/g, ''))}
                            isInvalid={formValidation['heatingYear']?.isValid == false}
                        />
                        <Form.Control.Feedback type="invalid">
                            {formValidation['heatingYear']?.warningText}
                        </Form.Control.Feedback>
                    </FloatingLabel>
                </Column>
                <Column lg={3} md={3}>
                    <Select
                        id="roofingUpdate"
                        label={cstPolicyUnderwritingLabel.ROOFING_UPDATE}
                        tabIndex={tbIndex++}
                        options={renovationOptions}
                        defaultSelected={renovationOptions.filter(opt => opt.value === policyHome?.roofingUpdate)}
                        onChange={values => handleFieldChange('roofingUpdate', values[0])}
                        placeholder={cstPolicyUnderwritingLabel.ROOFING_UPDATE}
                        showAsFloatingLabel={true}
                        className='mb-3'
                    />
                </Column>
                <Column lg={3} md={3}>
                    <FloatingLabel controlId="roofingYear" className="mb-3" label={cstPolicyUnderwritingLabel.ROOFING_YEAR}>
                        <Form.Control
                            type="text"
                            name="roofingYear"
                            tabIndex={tbIndex++}
                            placeholder={cstPolicyUnderwritingLabel.ROOFING_YEAR}
                            value={policyHome?.roofingYear || ''}
                            disabled={!CommonUtility.isRenovationSelected(policyHome?.roofingUpdate)}
                            maxLength={4}
                            pattern="[0-9]*"
                            onChange={e => handleFieldChange('roofingYear', e.currentTarget.value.replace(/[^0-9]/g, ''))}
                            isInvalid={formValidation['roofingYear']?.isValid == false}
                        />
                        <Form.Control.Feedback type="invalid">
                            {formValidation['roofingYear']?.warningText}
                        </Form.Control.Feedback>
                    </FloatingLabel>
                </Column>
            </Row>
            <Row>
                <Column lg={3} md={3}>
                    <FloatingLabel controlId="exteriorPaint" className="mb-3" label={cstPolicyUnderwritingLabel.EXTERIOR_PAINT_YEAR}>
                        <Form.Control
                            type="text"
                            name="exteriorPaint"
                            tabIndex={tbIndex++}
                            placeholder={cstPolicyUnderwritingLabel.EXTERIOR_PAINT_YEAR}
                            value={policyHome?.exteriorPaint || ''}
                            maxLength={4}
                            pattern="[0-9]*"
                            onChange={e => handleFieldChange('exteriorPaint', e.currentTarget.value.replace(/[^0-9]/g, ''))}
                            isInvalid={formValidation['exteriorPaint']?.isValid == false}
                        />
                        <Form.Control.Feedback type="invalid">
                            {formValidation['exteriorPaint']?.warningText}
                        </Form.Control.Feedback>
                    </FloatingLabel>
                </Column>
            </Row>
            </>
            )}
        </div>
    );
};

export default CustomerPolicyUnderwriting;